
import {defineComponent} from "vue";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import {CbJsonSalvusCalendarModel} from "@/models/api/pharma-cms-content-block/CbJsonSalvusCalendarModel";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {SalvusAppointmentTypesToShowEnum} from "@/api/enums/salvus-appointment-types-to-show-enum";
import Subtitle from "@/components/UI/Subtitle.vue";
import {NVPRestService} from "@/services/rest/master/NVPRestService";
import {CustomerContext} from "@/context/CustomerContext";
import {DpEvent} from "@/models/DpEvent";
import {SalvusHealthAuthentications} from "@/models/nvpair/SalvusHealthAuthentications";
import {
  SalvusHealthPharmacyAppointmentConfiguration,
  SalvusHealthRestService
} from "@/services/rest/SalvusHealthRestService";
import {SalvusHealthAuthentication} from "@/models/nvpair/SalvusHealthAuthentication";
import InputCheckbox from "@/components/UI/InputCheckbox.vue";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {JsonContentRestDtoModel} from "@/models/api/pharma-cms-content-block/JsonContentRestDtoModel";
import {ContentBlockEndpointsApiService} from "@/main";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";

export default defineComponent({
  name: "CbJsonSalvusCalendar",
  extends: CbStructureMixin,
  components: {
    InputCheckbox,
    InputSelect,
    InputCheckboxBoolean,
    BaseSpinner,
    Subtitle
  },
  data: () => ({
    salvusAuthentications: null as null|SalvusHealthAuthentications,
    contentBlock: CbJsonSalvusCalendarModel.createWithDefaults(),
    isNvpAvailable: false as boolean,

    activeSalvusAuthentication: null as null|SalvusHealthAuthentication,

    salvusAppointmentConfigurations: [] as SalvusHealthPharmacyAppointmentConfiguration[],

    apbNumberOptionsMap: [] as object[],

    salvusAppointmentIds: {} as Record<string, boolean>,
  }),
  computed: {
    SalvusAppointmentTypesToShowEnum() {
      return SalvusAppointmentTypesToShowEnum;
    },
    appointmentTypeOptions(): CodeDescriptionRestDto[] {
      return [
        {
          code: SalvusAppointmentTypesToShowEnum.ALL_AVAILABLE,
          description: this.$t("cms.salvus_appointmentTypeToShowEnum_ALL_AVAILABLE")
        },
        {
          code: SalvusAppointmentTypesToShowEnum.APPOINTMENTS,
          description: this.$t("cms.salvus_appointmentTypeToShowEnum_APPOINTMENTS")
        },
        {
          code: SalvusAppointmentTypesToShowEnum.RESERVATIONS,
          description: this.$t("cms.salvus_appointmentTypeToShowEnum_RESERVATIONS")
        },
        {
          code: SalvusAppointmentTypesToShowEnum.SPECIFIED,
          description: this.$t("cms.salvus_appointmentTypeToShowEnum_SPECIFIED")
        }
      ] as CodeDescriptionRestDto[];
    }
  },
  methods: {
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      const apiPayload = JsonContentRestDtoModel.createWithDefaults();
      if (payload.cb != null) {
        apiPayload.content = payload.cb;
      }
      const response = await ContentBlockEndpointsApiService.createNormalContentBlockJson(apiPayload as JsonContentRestDtoModel, payload.type);

      return response.data.id as string;
    },
    async find(payload: FindNormalContentBlockPayload): Promise<CbJsonSalvusCalendarModel> {
      const response = await ContentBlockEndpointsApiService.getNormalContentBlockJson(payload.type, payload.id);

      const responseData = response.data;
      let salvusCalendar = null;
      if (responseData.content != null) {
        salvusCalendar = Object.assign(CbJsonSalvusCalendarModel.createWithDefaults(), responseData.content);
      }

      return salvusCalendar as CbJsonSalvusCalendarModel;
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      const apiPayload = JsonContentRestDtoModel.createWithDefaults();
      if (payload.cb != null) {
        apiPayload.content = payload.cb;
      }
      await ContentBlockEndpointsApiService.updateNormalContentBlockJson(apiPayload as JsonContentRestDtoModel, payload.type, payload.id);
    },
    async onSalvusApbNumberChanged(apbNumber: string): Promise<void> {
      this.activeSalvusAuthentication = this.salvusAuthentications?.getAuthentication(apbNumber) ?? null;
      if (this.activeSalvusAuthentication === null) {
        return;
      }

      const configs: SalvusHealthPharmacyAppointmentConfiguration[] = await SalvusHealthRestService.getInstance()
        .getPharmacyAppointmentConfiguration(apbNumber, this.activeSalvusAuthentication);

      this.salvusAppointmentConfigurations = configs.filter((config: SalvusHealthPharmacyAppointmentConfiguration) => {
        return config.active;
      });

      this.salvusAppointmentIds = {};

      if (this.salvusAppointmentConfigurations.length === 0) {
        return;
      }

      this.contentBlock.salvus_pharmacy_code = this.salvusAppointmentConfigurations[0].appointmentLink;
      this.salvusAppointmentConfigurations.forEach((config: SalvusHealthPharmacyAppointmentConfiguration) => {
        this.salvusAppointmentIds[config.id] = false;
      });
    },
    async beforeSubmitData(): Promise<void> {
      this.contentBlock.appointment_identifiers = [];

      for (const [id, enabled] of Object.entries(this.salvusAppointmentIds)) {
        if (enabled) {
          this.contentBlock.appointment_identifiers.push(id);
        }
      }
    },
    async beforeReloadContent(event: DpEvent): Promise<void> {
      const nvp = await NVPRestService.getInstance()
        .getDpNvpByName(CustomerContext.getCustomerCode(), 'SALVUS_HEALTH_AUTHENTICATIONS', 'CMS');

      if (nvp === null) {
        this.isNvpAvailable = false;
        event.stopPropagation();
      }

      this.salvusAuthentications = Object.assign(new SalvusHealthAuthentications([]), JSON.parse(nvp.value));

      if (this.salvusAuthentications === null) {
        throw new Error('Could not construct SalvusHealthAuthentications');
      }

      if (!this.salvusAuthentications.hasAuthentications()) {
        this.isNvpAvailable = false;
        event.stopPropagation();
      }

      this.apbNumberOptionsMap = this.salvusAuthentications.getAuthenticationsForSelect();
      this.isNvpAvailable = true;
    },
    async afterReloadContent(): Promise<void> {
      await this.onSalvusApbNumberChanged(this.contentBlock.apb_number);

      const identifiers = this.contentBlock.appointment_identifiers ?? [];
      for (const id of identifiers) {
        this.salvusAppointmentIds[id] = true;
      }
    },
    getDefaultCbModel(): CbJsonSalvusCalendarModel {
      return CbJsonSalvusCalendarModel.createWithDefaults();
    },
    pasteCbMetadata(): void {
      // TODO?
    },
  }
});
